// Physically based sky: Rayleigh + Mie + ozone single scattering over a
// spherical planet (units: km), after Hillaire 2020 / Bruneton. Shared by the
// transmittance LUT, the sky-view LUT, the cloud pass and the background.

export const ATMOSPHERE_COMMON = /* glsl */ `
#define PLANET_R 6360.0
#define ATMO_R 6460.0
#define PI 3.14159265359
const vec3 RAY_S = vec3(5.802, 13.558, 33.1) * 1e-3;
const float MIE_S = 3.996e-3;
const float MIE_E = 4.40e-3;
const vec3 OZ_A = vec3(0.650, 1.881, 0.085) * 1e-3;
uniform float uMieScale;

vec3 atmExtinction(float h) {
  return RAY_S * exp(-h / 8.0) + vec3(MIE_E * uMieScale * exp(-h / 1.2)) + OZ_A * max(0.0, 1.0 - abs(h - 25.0) / 15.0);
}

// Distance to the far / near intersection with a sphere at the origin, or -1.
float raySphereFar(vec3 ro, vec3 rd, float r) {
  float b = dot(ro, rd);
  float c = dot(ro, ro) - r * r;
  float d = b * b - c;
  if (d < 0.0) return -1.0;
  return -b + sqrt(d);
}
float raySphereNear(vec3 ro, vec3 rd, float r) {
  float b = dot(ro, rd);
  float c = dot(ro, ro) - r * r;
  float d = b * b - c;
  if (d < 0.0) return -1.0;
  return -b - sqrt(d);
}

vec2 transmittanceUV(float h, float mu) {
  return vec2(0.5 + 0.5 * sign(mu) * sqrt(abs(mu)), sqrt(clamp(h / 100.0, 0.0, 1.0)));
}

float phaseRayleigh(float c) {
  return 3.0 / (16.0 * PI) * (1.0 + c * c);
}
float phaseMie(float c, float g) {
  float g2 = g * g;
  // Cornette-Shanks
  return 3.0 / (8.0 * PI) * ((1.0 - g2) * (1.0 + c * c)) / ((2.0 + g2) * pow(max(1.0 + g2 - 2.0 * g * c, 1e-4), 1.5));
}
float phaseHG(float c, float g) {
  float g2 = g * g;
  return (1.0 - g2) / (4.0 * PI * pow(max(1.0 + g2 - 2.0 * g * c, 1e-4), 1.5));
}
`;

export const TRANSMITTANCE_FRAG = /* glsl */ `
${ATMOSPHERE_COMMON}
in vec2 vUv;
void main() {
  float h = vUv.y * vUv.y * 100.0;
  float x = vUv.x * 2.0 - 1.0;
  float mu = sign(x) * x * x;
  vec3 ro = vec3(0.0, PLANET_R + h, 0.0);
  vec3 rd = vec3(sqrt(max(0.0, 1.0 - mu * mu)), mu, 0.0);
  if (raySphereNear(ro, rd, PLANET_R) > 0.0) {
    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }
  float tMax = raySphereFar(ro, rd, ATMO_R);
  vec3 od = vec3(0.0);
  const int N = 48;
  float dt = tMax / float(N);
  for (int i = 0; i < N; i++) {
    vec3 p = ro + rd * (float(i) + 0.5) * dt;
    od += atmExtinction(length(p) - PLANET_R) * dt;
  }
  gl_FragColor = vec4(exp(-od), 1.0);
}
`;

/**
 * Sky-view LUT: in-scattered radiance for every view direction around the
 * camera, parameterised by azimuth relative to the sun and a latitude mapping
 * that concentrates texels at the horizon.
 */
export const SKYVIEW_FRAG = /* glsl */ `
${ATMOSPHERE_COMMON}
uniform sampler2D uTrans;
uniform float uCamH;
uniform float uSunEl;
uniform vec3 uMoonLocal;
uniform float uMoonI;
in vec2 vUv;

vec3 trans(float h, float mu) {
  return texture(uTrans, transmittanceUV(h, mu)).rgb;
}

void main() {
  float az = vUv.x * 2.0 * PI;
  float v = vUv.y * 2.0 - 1.0;
  float lat = sign(v) * v * v * PI * 0.5;
  vec3 rd = vec3(cos(lat) * cos(az), sin(lat), cos(lat) * sin(az));
  vec3 sun = vec3(cos(uSunEl), sin(uSunEl), 0.0);
  vec3 moon = uMoonLocal;
  vec3 ro = vec3(0.0, PLANET_R + uCamH, 0.0);
  float tMax = raySphereFar(ro, rd, ATMO_R);
  float tG = raySphereNear(ro, rd, PLANET_R);
  if (tG > 0.0) tMax = tG;
  float cs = dot(rd, sun);
  float cm = dot(rd, moon);
  float prS = phaseRayleigh(cs), pmS = phaseMie(cs, 0.8);
  float prM = phaseRayleigh(cm), pmM = phaseMie(cm, 0.8);
  vec3 L = vec3(0.0);
  vec3 T = vec3(1.0);
  const int N = 36;
  float tPrev = 0.0;
  for (int i = 0; i < N; i++) {
    float f = (float(i) + 0.5) / float(N);
    float t = tMax * f * f;
    float dt = t - tPrev;
    tPrev = t;
    vec3 p = ro + rd * t;
    float r = length(p);
    float h = r - PLANET_R;
    vec3 up = p / r;
    vec3 ext = atmExtinction(h);
    vec3 sR = RAY_S * exp(-h / 8.0);
    float sM = MIE_S * uMieScale * exp(-h / 1.2);
    vec3 tS = trans(h, dot(up, sun));
    vec3 tM = trans(h, dot(up, moon));
    // Single scattering + a cheap isotropic multiple-scattering term.
    vec3 S = (sR * prS + sM * pmS) * tS + (sR + sM) * tS * 0.09;
    S += ((sR * prM + sM * pmM) * tM + (sR + sM) * tM * 0.09) * uMoonI;
    vec3 sampleT = exp(-ext * dt);
    L += T * (S - S * sampleT) / max(ext, vec3(1e-7));
    T *= sampleT;
  }
  gl_FragColor = vec4(L, 1.0);
}
`;

/** Look up the sky-view LUT (needs uSkyView, uSunAz). */
export const SKYVIEW_LOOKUP = /* glsl */ `
vec3 skyViewLookup(sampler2D lut, vec3 dir, float sunAz) {
  float az = atan(dir.z, dir.x) - sunAz;
  az = mod(az, 2.0 * PI);
  float lat = asin(clamp(dir.y, -1.0, 1.0));
  float v = sign(lat) * sqrt(abs(lat) / (PI * 0.5));
  return texture(lut, vec2(az / (2.0 * PI), 0.5 + 0.5 * v)).rgb;
}
`;
